import { type EditorWidget, type Theme } from 'mini-code'
import {
  type AmplitudeInfo,
  WAVEFORM_BUFFER_SIZE,
} from '../bytecode.ts'
import { dspscript } from '../dsp-script.ts'
import { waveformCanvases, waveformPointersMap, waveformReadOffsets } from './common.ts'

export class AmplitudeWidget {
  private widgetKey: string
  private waveformHeight = 60
  private waveformId: number
  private scrollRate = 60 // pixels per second
  private editorWidget: EditorWidget | null = null
  private contextId: string
  private shouldAnimate?: () => boolean

  constructor(
    private info: AmplitudeInfo,
    private theme: Theme,
    shouldAnimate?: () => boolean,
  ) {
    this.widgetKey = `amplitude-${info.id}-${info.line}-${info.column}`
    this.waveformId = info.id
    this.contextId = 'global'
    this.shouldAnimate = shouldAnimate
  }

  // Provide a way for callers to associate this widget with a context (tab id, track id, etc.)
  setContextId(contextId: string): void {
    this.contextId = contextId
  }

  private getWaveformKey(): string {
    return `${this.contextId}-${this.waveformId}`
  }

  toEditorWidget(): EditorWidget {
    if (this.editorWidget) return this.editorWidget

    this.editorWidget = {
      type: 'above',
      line: this.info.line,
      column: this.info.column,
      length: this.info.length,
      height: this.waveformHeight,

      render: (ctx, x, y, width, height) => {
        width -= 3
        height -= 2

        // Ensure valid dimensions
        if (width <= 0 || height <= 0) {
          return
        }

        ctx.fillStyle = this.theme.background
        ctx.fillRect(x, y, width, height)

        const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1
        const canvasWidth = Math.max(1, Math.floor(width * dpr))
        const canvasHeight = Math.max(1, Math.floor(height * dpr))

        const waveformKey = this.getWaveformKey()

        let canvasData = waveformCanvases.get(waveformKey)
        if (!canvasData) {
          const canvas = typeof OffscreenCanvas !== 'undefined'
            ? new OffscreenCanvas(canvasWidth, canvasHeight)
            : (() => {
              const el = document.createElement('canvas')
              el.width = canvasWidth
              el.height = canvasHeight
              return el
            })()

          const offscreenCtx = canvas.getContext('2d', {
            willReadFrequently: true,
          }) as OffscreenCanvasRenderingContext2D
          if (!offscreenCtx) {
            ctx.strokeStyle = 'rgba(100, 100, 100, 0.5)'
            ctx.strokeRect(x, y, width, height)
            return
          }

          offscreenCtx.imageSmoothingEnabled = false
          offscreenCtx.scale(dpr, dpr)

          offscreenCtx.fillStyle = this.theme.background
          offscreenCtx.fillRect(0, 0, width, height)

          const centerY = height / 2
          offscreenCtx.strokeStyle = '#666'
          offscreenCtx.lineWidth = 0.75
          offscreenCtx.beginPath()
          offscreenCtx.moveTo(0, centerY)
          offscreenCtx.lineTo(width, centerY)
          offscreenCtx.stroke()

          canvasData = { canvas, ctx: offscreenCtx }
          waveformCanvases.set(waveformKey, canvasData)
        }
        else if (canvasData.canvas.width !== canvasWidth || canvasData.canvas.height !== canvasHeight) {
          if (canvasData.canvas instanceof HTMLCanvasElement) {
            canvasData.canvas.width = canvasWidth
            canvasData.canvas.height = canvasHeight
            // Re-apply scale after resize (resizing clears the context)
            canvasData.ctx.imageSmoothingEnabled = false
            canvasData.ctx.scale(dpr, dpr)
            canvasData.ctx.fillStyle = this.theme.background
            canvasData.ctx.fillRect(0, 0, width, height)
            const centerY = height / 2
            canvasData.ctx.strokeStyle = '#666'
            canvasData.ctx.lineWidth = 0.75
            canvasData.ctx.beginPath()
            canvasData.ctx.moveTo(0, centerY)
            canvasData.ctx.lineTo(width, centerY)
            canvasData.ctx.stroke()
          }
          else if (canvasData.canvas instanceof OffscreenCanvas) {
            // OffscreenCanvas doesn't support resizing, need to recreate
            const newCanvas = new OffscreenCanvas(canvasWidth, canvasHeight)
            const newCtx = newCanvas.getContext('2d', { willReadFrequently: true })
            if (newCtx) {
              newCtx.imageSmoothingEnabled = false
              newCtx.scale(dpr, dpr)
              newCtx.fillStyle = this.theme.background
              newCtx.fillRect(0, 0, width, height)
              const centerY = height / 2
              newCtx.strokeStyle = '#666'
              newCtx.lineWidth = 0.75
              newCtx.beginPath()
              newCtx.moveTo(0, centerY)
              newCtx.lineTo(width, centerY)
              newCtx.stroke()
              canvasData = { canvas: newCanvas, ctx: newCtx }
              waveformCanvases.set(waveformKey, canvasData)
            }
          }
          else {
            canvasData.ctx.imageSmoothingEnabled = false
            canvasData.ctx.scale(dpr, dpr)
            canvasData.ctx.fillStyle = this.theme.background
            canvasData.ctx.fillRect(0, 0, width, height)
            const centerY = height / 2
            canvasData.ctx.strokeStyle = '#666'
            canvasData.ctx.lineWidth = 0.75
            canvasData.ctx.beginPath()
            canvasData.ctx.moveTo(0, centerY)
            canvasData.ctx.lineTo(width, centerY)
            canvasData.ctx.stroke()
          }
        }

        const { canvas: offscreenCanvas, ctx: offscreenCtx } = canvasData

        const isPlayingGlobal = dspscript.isPlaying()

        if (!isPlayingGlobal) {
          ctx.drawImage(offscreenCanvas, x, y, width, height)
          return
        }

        // Allow callers to override when animation should happen.
        if (this.shouldAnimate && !this.shouldAnimate()) {
          ctx.drawImage(offscreenCanvas, x, y, width, height)
          return
        }

        const wasmMemory = dspscript.getWasmMemory()
        const pointer = waveformPointersMap.get(this.waveformId)
        const writePositions = dspscript.getWaveformWritePositions()
        if (!wasmMemory || !pointer || !writePositions) {
          ctx.drawImage(offscreenCanvas, x, y, width, height)
          return
        }

        const bufferView = new Float32Array(wasmMemory, pointer.bufferPtr, WAVEFORM_BUFFER_SIZE)
        let readOffset = waveformReadOffsets.get(waveformKey)

        // Get write position from shared buffer (atomically read)
        const writePosition = Atomics.load(writePositions, this.waveformId)

        // Initialize read offset to write position if not set (start reading from current position)
        if (readOffset === undefined) {
          readOffset = writePosition
          waveformReadOffsets.set(waveformKey, readOffset)
        }

        // Calculate available samples (distance from read to write, wrapping around)
        const distance = (writePosition - readOffset + WAVEFORM_BUFFER_SIZE) % WAVEFORM_BUFFER_SIZE

        // Calculate desired samples per pixel
        const sampleRate = dspscript.getAudioContext()?.sampleRate ?? 48000
        const samplesPerPixel = Math.floor(sampleRate / this.scrollRate)

        // Read up to available samples or samples per pixel, whichever is less
        // Cap at buffer size to prevent reading more than available
        const samplesToRead = Math.min(samplesPerPixel, distance, WAVEFORM_BUFFER_SIZE)

        let peak = 0
        if (samplesToRead > 0) {
          for (let s = 0; s < samplesToRead; s++) {
            const idx = (readOffset + s) % WAVEFORM_BUFFER_SIZE
            peak = Math.max(peak, Math.abs(bufferView[idx]!))
          }

          // Update read offset to track where we've read up to
          readOffset = (readOffset + samplesToRead) % WAVEFORM_BUFFER_SIZE
          waveformReadOffsets.set(waveformKey, readOffset)
        }

        // Scroll canvas left by 1 pixel using getImageData/putImageData (works correctly with DPR)
        if (width > 1) {
          // getImageData/putImageData work in device pixels, not scaled coordinates
          const pixelsToScroll = Math.floor(1 * dpr)
          const sourceX = pixelsToScroll
          const sourceY = 0
          const sourceWidth = canvasWidth - pixelsToScroll
          const sourceHeight = canvasHeight
          const destX = 0
          const destY = 0

          if (sourceWidth > 0 && sourceHeight > 0) {
            const imageData = offscreenCtx.getImageData(sourceX, sourceY, sourceWidth, sourceHeight)
            offscreenCtx.putImageData(imageData, destX, destY)
          }

          // Clear the rightmost column (in logical coordinates since context is scaled)
          offscreenCtx.fillStyle = this.theme.background
          offscreenCtx.fillRect(width - 1, 0, 1, height)
        }

        // Draw new pixel on the right
        const drawX = width - 1
        const ampBarHeight = Math.max(1, peak * height)
        const ampY = (height - ampBarHeight) / 2

        if (Number.isFinite(peak) && Number.isFinite(ampBarHeight) && Number.isFinite(ampY)) {
          const grad = offscreenCtx.createLinearGradient(drawX, ampY, drawX, ampY + ampBarHeight)
          if (peak > 1) {
            grad.addColorStop(0, '#f00')
            grad.addColorStop(0.5, '#f00')
            grad.addColorStop(1, '#f00')
          }
          else {
            grad.addColorStop(0.2, '#999')
            grad.addColorStop(0.5, '#ddd')
            grad.addColorStop(0.8, '#999')
          }
          offscreenCtx.fillStyle = grad
          offscreenCtx.fillRect(drawX, ampY, 1, ampBarHeight)
        }

        ctx.drawImage(offscreenCanvas, x, y, width, height)
      },
    }

    return this.editorWidget
  }
}
